import { useEffect, useRef, useState } from "react";

/**
 * Observes when an element enters the viewport.
 * @param {Object} options
 * @param {number} options.threshold - 0–1 visibility ratio
 * @param {string} options.rootMargin - IntersectionObserver rootMargin
 * @param {boolean} options.once - Animate only once (default true)
 */
export function useInView({ threshold = 0.15, rootMargin = "0px 0px -40px 0px", once = true } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.unobserve(node);
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return [ref, inView];
}
